import { useContext } from 'react'

import {
  deepSetObject, digObject, launchModal, toggleArray,
} from '@domain-group/javascript-utils'

import { useForm, UseFormOptions } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'

import useProvider, { UseProviderPayload } from '@hooks/useProvider'

import defaultFormState, { requiredFields } from '@models/providerDeepLink'
import type { ProviderDeepLinkModel, ProviderModel } from '@models/types'

// [
//   {
//     headers: [{
//       key: 'Authorization',
//       value: 'Bearer API_KEY',
//     }],
//     id: 'TWbyCm53',
//     link_text: 'Manage Booking',
//     method: 'GET',
//     status_id: 1,
//     title: 'Signboards #1',
//     url: 'https://api.sprintmedia.com.au/import/realhub/v1?existing=test',
//   },
// ]

type AddOrEditProviderDeepLinkParams = {
  providerDeepLink: ProviderDeepLinkModel,
  mode?: 'createOrEdit' | 'delete',
  provider: ProviderModel,
  updateProvider: UseProviderPayload['callbacks']['updateProvider'],
}

const updateProviderDeepLinks = (params: AddOrEditProviderDeepLinkParams) => {
  const {
    providerDeepLink, mode, provider, updateProvider,
  } = params

  const deepLinks = digObject(provider, 'options.deep_links', [])
  const toggleArrayOptions = mode === 'delete' ? { deepCompare: true } : { useObjectKey: 'id' }

  const updatedDeepLinks = toggleArray(deepLinks, providerDeepLink, toggleArrayOptions)

  // @ts-expect-error TS2345
  const updatedOptions = deepSetObject(provider.options, 'deep_links', updatedDeepLinks)

  return updateProvider({ options: updatedOptions })
}

export function useProviderDeepLinkForm(
  providerDeepLink: Partial<ProviderDeepLinkModel> = {},
  options: UseFormOptions = {},
) {
  const { validateOn } = options

  const providerDeepLinkForm = useForm(
    defaultFormState,
    { entity: providerDeepLink, requiredFields, validateOn },
    [providerDeepLink.id],
  )

  return {
    ...providerDeepLinkForm,
  }
}

const deepLinkStatusKeys = [
  'approved',
  'held',
  'packaged',
  'pending',
  'printed',
  'processed',
  'processing',
] as const

type UpdateDeepLinkParams = { providerDeepLink: ProviderDeepLinkModel, mode: 'createOrEdit' | 'delete' }

function useProviderDeepLink(providerDeepLink?: ProviderDeepLinkModel, provider?: ProviderModel) {
  const { callbacks: { updateProvider }, updating } = useProvider(provider)

  const { callbacks } = useContext(PageContext)

  return {
    callbacks: {
      createOrEditProviderDeepLink: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditProviderDeepLinkModal',
        payload: { provider, providerDeepLink },
      }),
      updateProviderDeepLinks: (params: UpdateDeepLinkParams) => (
        // @ts-expect-error TS2322
        updateProviderDeepLinks({ ...params, provider, updateProvider })
      ),
    },
    deepLinkStatusKeys,
    updating,
  }
}

export type UseProviderDeepLinkPayload = ReturnType<typeof useProviderDeepLink>

export default useProviderDeepLink
